import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './OnePager.module.scss';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import DealSummaryEditorPlate from './editor/DealSummaryEditorPlate';
import { generateDocumentFromTemplate, getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { closeOnePager, setOnePagerData } from '../../store/investor/action';
import { Button, Modal } from '../_custom';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';
import { unwrapDocVersion, runMigrations } from '../PlateEditor/migrations';

interface OnePagerProps {
    companyName?: string;
    metadataUpdate?: (fileInfo: { url?: string; fileName?: string; docId?: string | number }) => void;
    categoryId?: number;
    flowId?: number;
}

function OnePager({ companyName, metadataUpdate, categoryId, flowId }: OnePagerProps) {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const onePagerState = useAppSelector((state) => state.investor.onePager);
    const assesseeOrgId = onePagerState.assesseeOrgId;
    const source = onePagerState.source;
    const resolvedCompanyName = companyName || (onePagerState as any).companyName || '';
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);

    useEffect(() => {
        return () => {
            dispatch(closeOnePager());
        };
    }, []);

    useEffect(() => {
        fetchOnePagerTemplateData();
    }, [assesseeOrgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    // Generates a fresh One Pager via the composer service when no saved doc exists
    // (or on regenerate). Replaces the legacy state-machine + polling path.
    const fetchDataForOnePager = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        dispatch(setOnePagerData({ loading: true }));
        try {
            const result = await generateDocumentFromTemplate({
                assesseeOrgId: assesseeOrgId as string,
                templateId: 'ONE_PAGER',
            });
            const value = result.document ?? [{ type: 'p', children: [{ text: '' }] }];
            setEditorInitialData(sanitizeSlateValue(value));
            setTimeout(() => {
                setEditorReady(true);
                dispatch(setOnePagerData({ loading: false }));
            }, 200);
        } catch (err) {
            console.error('One Pager generation error:', err);
            toast.error('Could not generate the One Pager. Please try again.');
            dispatch(setOnePagerData({ loading: false }));
            // Regenerating an already-open doc: keep the drawer open with its existing
            // content rather than closing it out from under the user's edits. On the
            // initial fetch there is no existing content to preserve, so close instead
            // of leaving editorReady=false forever (a permanently blank drawer).
            if (isRegenerate) {
                setEditorReady(true);
            } else {
                dispatch(closeOnePager());
            }
        }
    };

    const fetchOnePagerTemplateData = async () => {
        setEditorReady(false);
        dispatch(setOnePagerData({ loading: true }));
        const onePagerTemplateResponse = await getOnePagerTemplateData({
            assesseeOrgId: assesseeOrgId as string,
            investorOrgId: investorOrgId,
            docType: 'ONE_PAGER',
        });
        const { onePagerData, templateData } = onePagerTemplateResponse;
        const { version, value } = unwrapDocVersion(templateData);
        if (value?.length) {
            const migrated = runMigrations(version, value);
            const sanitizedData = sanitizeSlateValue(migrated);
            setEditorInitialData(sanitizedData);
            setUserInfo(onePagerData);
            setTimeout(() => {
                setEditorReady(true);
                dispatch(setOnePagerData({ loading: false }));
            }, 200);
        } else fetchDataForOnePager();
    };

    // `isDraft = true` → autosave: upsert the single live draft for this
    // (org, docType). It must NOT take the committing-save guard, must NOT close
    // the drawer, and must NOT mark the doc as restored — it's a silent
    // background persist. `isDraft = false` (Save & Close / restore) commits:
    // the backend flips the live draft to a committed version.
    const saveOnePagerTemplateDataForOrg = (editorContent, keepDrawerOpen = false, onSuccess, isDraft = false) => {
        if (!isDraft && savingData) return;
        if (!isDraft) setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: assesseeOrgId as string,
            investorOrgId: investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            isRestored: keepDrawerOpen && !isDraft,
            isDraft,
        })
            .then(() => {
                if (isDraft) {
                    onSuccess?.();
                    return;
                }
                setTimeout(() => {
                    if (!keepDrawerOpen) {
                        dispatch(closeOnePager());
                    }
                    setSavingData(false);
                    onSuccess?.();
                }, 1000);
            })
            .catch(() => {
                if (!isDraft) setSavingData(false);
            });
    };

    // V2-only: the legacy Slate-based DealSummaryEditor was removed. The
    // `editor.onePager.v2` key is retained in visibilityKeys.ts for reference/revert
    // but is no longer read here.
    const EditorComponent: any = DealSummaryEditorPlate;

    return (
        <div className={styles.OnePager}>
            {(source === 'CREATE_DEAL' ? onePagerState.open : editorReady) && (
                <EditorComponent
                    openModal={source === 'CREATE_DEAL' ? true : editorReady}
                    closeModal={() => dispatch(closeOnePager())}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    generating={onePagerState.loading}
                    companyNameProp={resolvedCompanyName}
                    userInfo={userInfo}
                    assesseeOrgId={assesseeOrgId ?? undefined}
                    saveOnePagerTemplateDataForOrg={saveOnePagerTemplateDataForOrg}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    source={source}
                    categoryId={categoryId}
                    flowId={flowId}
                    metadataUpdate={metadataUpdate}
                    savingData={savingData}
                />
            )}

            {showRegenConfirmModal && (
                <Modal
                    open={showRegenConfirmModal}
                    onClose={() => setShowRegenConfirmModal(false)}
                    size="flexible"
                    className={styles.RegenModalOverDrawer}
                >
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate One Pager</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate One pager? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                }}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForOnePager(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default OnePager;
